import { IBaseArrayResultDto } from '@skillarea/lib-shared';
import { queryOptions, QueryClient } from '@tanstack/react-query';

export interface IQueryData<TId extends string | number, T> {
  items: Record<TId, T>;
  cursor?: string;
  lastCursor?: string;
}

// Common entity shape for incremental queries
interface IEntityWithIdAndUpdatedAt {
  id: string | number;
  updatedAt: Date;
}

/**
 * Gets items count from query data
 */
function getItemsCount<TId extends string | number, T>(
  data: IQueryData<TId, T> | undefined,
): number {
  return data ? Object.keys(data.items).length : 0;
}

/**
 * Creates incremental query options with support for:
 * - Polling (updatedAt) - merges new/updated records
 * - Cursor pagination - loads next pages
 *
 * Usage:
 * ```ts
 * const options = createIncrementalOptions('match', (args) => api.readAll(args));
 * const { data } = useSuspenseQuery(options({ limit: 100 }, queryClient);
 * const items = getItems(data);
 * const hasNext = hasNextPage(data, args);
 *
 * // Fetch next page
 * await fetchNextPage('match', { limit: 100 }, queryClient, lastCursor);
 * ```
 */
export function createIncrementalOptions<
  T extends IEntityWithIdAndUpdatedAt,
  TArgs extends Record<string, any>,
  TId extends T['id'] = T['id'],
>(
  baseKey: string,
  fetcher: (
    args: TArgs & { cursor?: string; updatedAt?: Date },
  ) => Promise<IBaseArrayResultDto<T>>,
) {
  return (args: TArgs, queryClient: QueryClient) => {
    const queryKey = [baseKey, args];

    return queryOptions({
      queryKey,
      staleTime: Infinity,
      queryFn: async () => {
        // Get cached data to preserve cursor
        const cached = queryClient.getQueryData<IQueryData<TId, T>>(queryKey);
        const cursor = cached?.lastCursor || args.cursor;

        // Determine mode: cursor pagination OR polling (updatedAt)
        const isPolling = 'updatedAt' in args && args.updatedAt !== undefined;
        // Initial load (no cursor, no updatedAt) behaves like pagination
        const isPagination = !isPolling;

        // Prepare fetch args
        const fetchArgs: TArgs & { cursor?: string; updatedAt?: Date } = {
          ...args,
        };

        if (isPagination) {
          // Cursor pagination mode - use cursor from cached or args
          fetchArgs.cursor = cursor;
        } else if (isPolling) {
          // Polling mode - use lastUpdated from cache
          fetchArgs.updatedAt = cached?.items
            ? getLastUpdated(Object.values(cached.items) as T[])
            : args.updatedAt;
        }

        // Fetch from API
        const rawResponse = await fetcher(fetchArgs);

        let responseData: T[];
        if (Array.isArray(rawResponse)) {
          responseData = rawResponse;
        } else if (Array.isArray(rawResponse.data)) {
          responseData = rawResponse.data;
        } else {
          if (import.meta.env.DEV) {
            const kind =
              rawResponse && typeof rawResponse === 'object'
                ? 'object'
                : typeof rawResponse;
            const dataType =
              rawResponse &&
              typeof rawResponse === 'object' &&
              'data' in rawResponse
                ? typeof (rawResponse as { data: unknown }).data
                : 'n/a';
            console.warn(
              '[createIncrementalOptions] Expected array or { data: array[] }, got response type:',
              kind,
              'data:',
              dataType,
            );
          }
          responseData = [];
        }

        // Adapt response to IBaseArrayResultDto
        const response = {
          success: true,
          error: '',
          data: responseData,
        };

        if (!response.success) throw new Error(response.error);

        // Merge or replace based on mode
        let itemsMap: Record<TId, T>;
        let lastCursor: string | undefined;

        if (isPagination) {
          // Pagination mode - replace items, track cursor
          itemsMap = {} as Record<TId, T>;
          response.data.forEach((item) => {
            (itemsMap as Record<string | number, T>)[item.id] = item;
          });
          const lastItem = response.data[response.data.length - 1];
          const cursorSource = lastItem as unknown as {
            createdAt?: string | Date;
            id: string | number;
          };
          lastCursor =
            response.data.length > 0
              ? String(cursorSource.createdAt ?? cursorSource.id)
              : undefined;
        } else {
          // Polling mode - merge with existing items
          itemsMap = { ...(cached?.items || {}) } as Record<TId, T>;
          response.data.forEach((item) => {
            (itemsMap as Record<string | number, T>)[item.id] = item;
          });
          // Preserve cursor from pagination state
          lastCursor = cached?.cursor || cached?.lastCursor;
        }

        return {
          items: itemsMap,
          cursor,
          lastCursor,
        };
      },
    });
  };
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Gets items array from query data
 */
export function getItems<TId extends string | number, T>(
  data: IQueryData<TId, T> | undefined,
): T[] {
  return data ? (Object.values(data.items) as T[]) : [];
}

/**
 * Gets current cursor from query data
 */
export function getCursor<TId extends string | number, T>(
  data: IQueryData<TId, T> | undefined,
): string | undefined {
  return data?.cursor;
}

/**
 * Gets last cursor from query data (after fetch)
 */
export function getLastCursor<TId extends string | number, T>(
  data: IQueryData<TId, T> | undefined,
): string | undefined {
  return data?.lastCursor;
}

/**
 * Checks if there is a next page available
 */
export function hasNextPage<TId extends string | number, T>(
  data: IQueryData<TId, T> | undefined,
  args: Record<string, any>,
  limit?: number,
): boolean {
  if (!data) return false;
  const itemsCount = getItemsCount(data);
  // If loaded items < limit, this is the last page
  // If loaded items >= limit AND has lastCursor, there's more
  const actualLimit = limit || (args.limit as number) || 100;
  return itemsCount >= actualLimit && !!data.lastCursor;
}

/**
 * Fetches next page using cursor pagination
 */
export async function fetchNextPage(
  baseKey: string,
  args: Record<string, any>,
  queryClient: QueryClient,
  cursor?: string,
): Promise<void> {
  const queryKey = [baseKey, { ...args, cursor }];

  // Invalidate cache to trigger refetch with new cursor
  await queryClient.invalidateQueries({ queryKey });
}

/**
 * Gets last updated timestamp from items
 */
function getLastUpdated(items: IEntityWithIdAndUpdatedAt[]): Date {
  if (items.length === 0) return new Date(0);
  let maxDate = new Date(items[0].updatedAt);
  for (let i = 1; i < items.length; i++) {
    const itemDate = new Date(items[i].updatedAt);
    if (itemDate > maxDate) maxDate = itemDate;
  }
  return maxDate;
}
